---
title: "Chosen glossary"
description: "Plain definitions of the terms you'll meet in Chosen — candidate, prospect, pipeline, match rating, smart and deep search, mission, Choosie, and more."
category: "reference"
keywords:
  - "Chosen glossary"
  - "ATS terms"
  - "recruiting software terms"
  - "candidate vs prospect"
schema: "Article"
nav:
  section: "reference"
  order: 10
---

Chosen has its own vocabulary, and a few words mean something specific here. This page defines them. Each term links to the guide that covers it in full.

## People

**Candidate** — someone in your hiring pipeline for a job. They either applied through your careers page or were added by you. Candidates live in the [candidates table](/guides/candidates), have a [status](/guides/candidates/statuses-and-pipeline), and can carry a [match rating](/guides/candidates/match-rating).

**Prospect** — someone you found through [sourcing](/guides/sourcing) who hasn't entered a hiring pipeline. Prospects are people you reach out to. A prospect who replies and wants to talk gets [converted into a candidate](/guides/sourcing/prospect-to-candidate) — that's the moment they cross over.

**Applicant** — a candidate, viewed in the context of one job application. The same person can be an applicant on two jobs at once.

## Pipeline

**Status** (also called **stage**) — where a candidate sits in your hiring process: applied, screening, interview, offer, and so on. You move candidates between statuses by dragging them on the [board](/guides/candidates/table-and-boards). The two words mean the same thing.

**Pipeline** — the ordered set of statuses a candidate moves through for a job, start to finish. [Statuses and the pipeline](/guides/candidates/statuses-and-pipeline) covers how it's structured.

**Match rating** — a 0–100% score estimating how well a candidate's resume fits a specific job. It's derived from the job description, recomputes when that description changes, and shows the evidence behind the number. Full detail in [match rating](/guides/candidates/match-rating).

## Search and sourcing

**Smart search** — the instant, everyday search. Type a plain sentence and it searches the candidates already in Chosen. See [smart, boolean, and deep search](/guides/search/smart-vs-deep).

**Deep search** — an AI search that reads resume bodies and scores fit. Slower than smart search and metered, it's for queries that need judgment. [How a deep search run works](/guides/search/deep-search-runs) walks through one.

**Mission** — a sourcing run that goes out and finds new people. Each [sourcing search](/guides/sourcing/find-prospects) you launch is a mission. Internally, that's also the word for the row that records it.

**Enrichment** — looking up a prospect's contact details, mainly a verified email address, so you can reach them. Covered in [enrichment](/guides/sourcing/enrichment).

**List** — a static group of prospects assembled for one outreach campaign. The members don't change unless you change them. See [sending outreach](/guides/sourcing/outreach-sending).

## Assistant and scheduling

**Choosie** — the AI assistant inside Chosen. You ask it questions in plain language; it searches and acts, holding any change for your approval first. Start with [Choosie](/choosie).

**Plan** — what Choosie writes instead of immediately changing your data: a rationale plus the exact steps it intends to run. Nothing happens until you approve it. See [plans and approvals](/choosie/plans-and-approvals).

**Scheduling request** — an interview Chosen's [AI scheduler](/guides/scheduling/ai-scheduler-overview) is coordinating. It drafts the emails, reads replies, and proposes times, then waits for you to confirm.

**Availability window** — the blocks of time you mark as open for interviews. The scheduler only offers candidates slots inside them. See [availability windows](/guides/scheduling/availability-windows).

## Account

**Organization** — your company's workspace in Chosen. All your jobs, candidates, and settings live inside it, and it's never visible to another company. Setup is covered in [getting started](/getting-started).

**Role** — what a teammate is allowed to do: Owner, Admin, or Viewer. Roles are explained in [accounts and roles](/getting-started/accounts-and-roles).

**Plan** (Choosie) — a proposed change Choosie lays out for your approval before it acts. Chosen itself has no billing plans — the ATS is free.
